import { useCallback, useEffect, useRef } from 'react';
import { TabCounterManager } from '../utils/tabCounterManager';

interface UseTabCounterOptions {
  /** Título original da página */
  originalTitle?: string;
  /** Considerar mensagens de grupos no contador */
  includeGroups?: boolean;
  /** Separador entre contador e título */
  separator?: string;
}

/**
 * Hook React para gerenciar o contador de mensagens não lidas na aba do navegador
 * Implementado de forma performática para evitar re-renderizações
 */
export function useTabCounter(options: UseTabCounterOptions = {}) {
  const counterRef = useRef<TabCounterManager | null>(null);
  const lastCountRef = useRef<number>(0);
  const optionsRef = useRef(options);

  // Inicializar contador apenas uma vez
  useEffect(() => {
    counterRef.current = new TabCounterManager(options);

    // Cleanup ao desmontar
    return () => {
      if (counterRef.current) {
        counterRef.current.destroy();
        counterRef.current = null;
      }
    };
  }, []);

  // Atualizar configurações quando mudarem (com debounce)
  useEffect(() => {
    if (counterRef.current) {
      const hasChanged =
        optionsRef.current.includeGroups !== options.includeGroups ||
        optionsRef.current.originalTitle !== options.originalTitle ||
        optionsRef.current.separator !== options.separator;

      if (hasChanged) {
        optionsRef.current = options;
        counterRef.current.updateConfig(options);
      }
    }
  }, [options.includeGroups, options.originalTitle, options.separator]);

  /**
   * Atualiza o contador de mensagens não lidas
   * Implementado com memoização para evitar chamadas desnecessárias
   */
  const updateCounter = useCallback((count: number) => {
    // Evitar atualizações desnecessárias
    if (count === lastCountRef.current) {
      return;
    }

    lastCountRef.current = count;
    counterRef.current?.updateCounter(count);
  }, []);

  /**
   * Força atualização imediata do contador
   */
  const forceUpdate = useCallback((count: number) => {
    lastCountRef.current = count;
    counterRef.current?.forceUpdate(count);
  }, []);

  /**
   * Calcula o total de mensagens não lidas considerando a configuração de grupos e instâncias do usuário
   */
  const calculateUnreadCount = useCallback((conversas: any[], userInstances?: any[]) => {
    if (!Array.isArray(conversas)) return 0;

    // Log para debugging
    // console.log('[TabCounter] Calculando contador:', {
    //   totalConversas: conversas.length,
    //   userInstancesCount: userInstances?.length || 0,
    //   includeGroups: options.includeGroups
    // });

    return conversas.reduce((total, conversa) => {
      // Verificar se é grupo e se deve ser considerado
      const isGroup = conversa.isGroup || conversa.ultimaMensagem?.isGrupo;
      if (!options.includeGroups && isGroup) {
        // console.log('[TabCounter] Ignorando mensagem de grupo:', conversa.id);
        return total;
      }

      // Verificar se o usuário tem acesso à instância desta conversa
      if (userInstances && userInstances.length > 0) {
        const conversaInstanceId = conversa.instancia?.id || conversa.instanciaUazapi?.id;
        const hasAccess = userInstances.some((instance: any) => instance.id === conversaInstanceId);

        if (!hasAccess || !conversaInstanceId) {
          // console.log('[TabCounter] Sem acesso à instância:', {
          //   conversaId: conversa.id,
          //   conversaInstanceId,
          //   hasAccess
          // });
          return total;
        }
      }

      // Adicionar mensagens não lidas
      const unreadCount = conversa.naoLidas || 0;
      // if (unreadCount > 0) {
      //   console.log('[TabCounter] Adicionando mensagens não lidas:', {
      //     conversaId: conversa.id,
      //     unreadCount,
      //     total: total + unreadCount
      //   });
      // }

      return total + unreadCount;
    }, 0);
  }, [options.includeGroups]);

  /**
   * Atualiza o contador baseado na lista de conversas e instâncias do usuário
   */
  const updateFromConversations = useCallback((conversas: any[], userInstances?: any[]) => {
    const count = calculateUnreadCount(conversas, userInstances);
    updateCounter(count);
  }, [calculateUnreadCount, updateCounter]);

  return {
    updateCounter,
    forceUpdate,
    updateFromConversations,
    calculateUnreadCount,
    getCurrentCount: () => counterRef.current?.getCurrentCount() || 0
  };
}

export default useTabCounter;
